/**
 * Re-consent prompt: asks a signed-in user to accept the CURRENT version of the
 * signup documents when the version in force has moved past what they agreed to.
 *
 * WHY THIS EXISTS. `GET /legal/acceptance-status` and `POST /legal/accept` have
 * been mounted and correct for some time with NO consumer at either end —
 * nothing asked whether a user was on the current terms, and nothing offered
 * them the new ones. That is invisible while only one version exists, and
 * becomes a compliance incident the moment new terms publish: every existing
 * customer continues under the old version, with no prompt and no way to record
 * their acceptance. WP-6.6 (the legal pack) is the trigger, so this is wired
 * BEFORE the terms change rather than after.
 *
 * DELIBERATELY NOT A HARD LOCKOUT. The backend `up_to_date` flag is the gate,
 * but a gate that fails CLOSED on a network error would lock every user out of
 * the product for the duration of an unrelated outage. So:
 *   - the prompt renders only on a definitive `outstanding` answer from the API
 *   - any error, or any anonymous visitor, renders NOTHING
 *   - the prompt is modal (no dismiss control) because a re-consent that can be
 *     clicked away records nothing and answers nobody, but it never blocks a
 *     request that is already in flight
 */

import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { m, AnimatePresence } from "framer-motion";
import { getAccessToken } from "@/api/client";
import { legalApi, type LegalOutstandingDoc } from "@/api/services";

/** Human label for a document slug; falls back to the slug itself. */
const DOC_LABELS: Record<string, string> = {
  terms: "Terms of Service",
  privacy: "Privacy Policy",
  dpa: "Data Processing Addendum",
  "recording-consent": "Recording Consent",
};

function label(slug: string): string {
  return DOC_LABELS[slug] ?? slug;
}

export function TermsReacceptGate() {
  const [outstanding, setOutstanding] = useState<LegalOutstandingDoc[] | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    // Anonymous visitors have nothing to re-accept and no identity to record it
    // against — the signup flow covers them.
    if (!getAccessToken()) return;
    let cancelled = false;
    legalApi
      .acceptanceStatus()
      .then((res) => {
        if (cancelled) return;
        // Trust the SHAPE, not just the promise. The a11y harness fulfils every
        // unmatched /api/v1/* URL with `{}`, and an earlier version of this did
        // `setOutstanding(res.outstanding)` -> undefined, then threw on
        // `outstanding.length` during render -- on every surface, in both
        // themes. Failing open on a rejection is not enough; a 200 carrying an
        // unexpected body is the case that actually took the page down.
        const list = res && Array.isArray(res.outstanding) ? res.outstanding : [];
        setOutstanding(res && res.up_to_date ? [] : list);
      })
      .catch(() => {
        // Fail OPEN. An unreachable status endpoint must not gate the product.
        if (!cancelled) setOutstanding([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const accept = useCallback(() => {
    setSubmitting(true);
    setFailed(false);
    legalApi
      .accept()
      .then(() => setOutstanding([]))
      .catch(() => {
        setFailed(true);
        setSubmitting(false);
      });
  }, []);

  const show = Array.isArray(outstanding) && outstanding.length > 0;

  return (
    <AnimatePresence>
      {show && (
        <m.div
          key="terms-reaccept"
          role="dialog"
          aria-modal="true"
          aria-labelledby="terms-reaccept-title"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4"
        >
          <m.div
            initial={{ y: 12, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 12, opacity: 0 }}
            className="w-full max-w-lg rounded-xl bg-white p-6 shadow-xl dark:bg-neutral-900"
          >
            <h2
              id="terms-reaccept-title"
              className="text-lg font-semibold text-neutral-900 dark:text-neutral-100"
            >
              Please review our updated terms
            </h2>
            <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-400">
              {outstanding!.length === 1
                ? "One of the documents you agreed to has been updated."
                : "Some of the documents you agreed to have been updated."}{" "}
              Please review and accept to continue.
            </p>
            <ul className="mt-4 space-y-2">
              {outstanding!.map((doc) => (
                <li
                  key={doc.document_slug}
                  className="flex items-center justify-between rounded-lg border border-neutral-200 px-3 py-2 dark:border-neutral-700"
                >
                  <Link
                    to={`/legal/${doc.document_slug}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-sm font-medium text-blue-600 underline hover:text-blue-700 dark:text-blue-400"
                  >
                    {label(doc.document_slug)}
                  </Link>
                  <span className="text-xs text-neutral-500 dark:text-neutral-400">
                    {doc.accepted_version
                      ? `v${doc.accepted_version} → v${doc.current_version}`
                      : `v${doc.current_version}`}
                  </span>
                </li>
              ))}
            </ul>
            {failed && (
              <p role="alert" className="mt-3 text-sm text-red-600 dark:text-red-400">
                We could not record that just now. Please try again.
              </p>
            )}
            <button
              type="button"
              onClick={accept}
              disabled={submitting}
              className="mt-5 w-full rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
            >
              {submitting ? "Recording…" : "I accept the updated terms"}
            </button>
          </m.div>
        </m.div>
      )}
    </AnimatePresence>
  );
}
